import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useFeature } from '../../../hooks/useFeature';
import { useTenant } from '../../../contexts/TenantContext';

/**
 * Mobile bottom-nav for admin pages.
 * Quick access bar (4 fixed items) + slide-up "More" sheet that lists
 * all admin pages, gated by feature flags.
 */
const QuickItem = ({ to, label, icon, active }) => (
  <Link
    to={to}
    className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 transition-colors ${
      active ? 'text-primary' : 'text-muted-foreground'
    }`}
  >
    <span className="text-lg">{icon}</span>
    <span className="text-[10px] font-medium">{label}</span>
  </Link>
);

const SheetItem = ({ to, label, icon, active, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex flex-col items-center justify-center gap-1.5 py-3 rounded-xl border transition-colors ${
      active
        ? 'bg-primary/10 border-primary/30 text-primary'
        : 'bg-surface-2 border-transparent text-muted-foreground hover:border-border hover:text-foreground'
    }`}
  >
    <span className="text-xl">{icon}</span>
    <span className="text-[11px] font-medium leading-tight text-center px-1">{label}</span>
  </Link>
);

const AdminBottomNav = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const hasCashback = useFeature('cashback');
  const { tenantSlug } = useTenant();
  const isTcn = tenantSlug === 'tcn';

  const quickItems = isTcn
    ? [
        { to: '/admin/dashboard',         label: t('admin.nav.dashboard', 'Home'),     icon: '◧' },
        { to: '/admin/creators',          label: t('admin.nav.creators', 'Creators'),  icon: '◉' },
        { to: '/admin/objective-rules',   label: t('admin.nav.rules', 'Rules'),        icon: '◎' },
      ]
    : [
        { to: '/admin/dashboard',   label: t('admin.nav.dashboard', 'Home'),     icon: '◧' },
        { to: '/admin/creators',    label: t('admin.nav.creators', 'Creators'),  icon: '◉' },
        { to: '/admin/performance', label: t('admin.nav.performance', 'Perf.'), icon: '◔' },
      ];

  const sections = useMemo(() => isTcn ? [
    {
      label: t('admin.sidebar.sections.tcn.overview', 'Overview'),
      items: [
        { to: '/admin/dashboard', label: t('admin.sidebar.items.dashboard', 'Dashboard'), icon: '◧' },
      ],
    },
    {
      label: t('admin.sidebar.sections.tcn.ingestion', 'Ingestion'),
      items: [
        { to: '/tcn/admin/upload',  label: t('admin.sidebar.items.upload', 'Upload Excel'), icon: '⇪' },
        { to: '/tcn/admin/imports', label: t('admin.sidebar.items.imports', 'History'), icon: '⌛' },
      ],
    },
    {
      label: t('admin.sidebar.sections.tcn.team', 'Team'),
      items: [
        { to: '/admin/users',    label: t('admin.sidebar.items.managers', 'Managers'), icon: '◭' },
        { to: '/admin/creators', label: t('admin.sidebar.items.creators', 'Creators'), icon: '◉' },
      ],
    },
    {
      label: t('admin.sidebar.sections.tcn.rules', 'Rules & objectives'),
      items: [
        { to: '/admin/objective-rules', label: t('admin.sidebar.items.objectiveRules', 'Objective rules'), icon: '◎' },
      ],
    },
    {
      label: t('admin.sidebar.sections.administration', 'Administration'),
      items: [
        { to: '/admin/profile', label: t('admin.sidebar.items.profile', 'Profile'), icon: '◕' },
      ],
    },
  ] : [
    {
      label: t('admin.sidebar.sections.data', 'Data'),
      items: [
        { to: '/admin/dashboard',     label: t('admin.sidebar.items.dashboard', 'Dashboard'), icon: '◧' },
        { to: '/admin/performance',   label: t('admin.sidebar.items.performance', 'Performance'), icon: '◔' },
        { to: '/admin/performances',  label: t('admin.sidebar.items.history', 'History'), icon: '⌛' },
      ],
    },
    {
      label: t('admin.sidebar.sections.creators', 'Creators'),
      items: [
        { to: '/admin/creators', label: t('admin.sidebar.items.creators', 'Creators'), icon: '◉' },
        ...(hasCashback ? [
          { to: '/admin/cashback',          label: t('admin.sidebar.items.cashback', 'Cashback'), icon: '⊕' },
          { to: '/admin/cashback-payments', label: t('admin.sidebar.items.cashbackPayments', 'Cashback pmts'), icon: '⊠' },
        ] : []),
      ],
    },
    {
      label: t('admin.sidebar.sections.team', 'Team'),
      items: [
        { to: '/admin/users',            label: t('admin.sidebar.items.users', 'Users'), icon: '◬' },
        { to: '/admin/manager-list',     label: t('admin.sidebar.items.managers', 'Managers'), icon: '◭' },
        { to: '/admin/groups',           label: t('admin.sidebar.items.groups', 'Groups'), icon: '⌗' },
        { to: '/admin/objectives',       label: t('admin.sidebar.items.objectives', 'Objectives'), icon: '◎' },
        { to: '/admin/objective-rules',  label: t('admin.sidebar.items.objectiveRules', 'Objective rules'), icon: '◎' },
        { to: '/admin/manager-rates',    label: t('admin.sidebar.items.rates', 'Rates'), icon: '%' },
        { to: '/admin/manager-payments', label: t('admin.sidebar.items.managerPayments', 'Pmts'), icon: '$' },
      ],
    },
    {
      label: t('admin.sidebar.sections.communication', 'Communication'),
      items: [
        { to: '/admin/notifications',       label: t('admin.sidebar.items.notifications', 'Notifs'), icon: '◐' },
        { to: '/admin/notifications/rules', label: t('admin.sidebar.items.notificationsRules', 'Rules'), icon: '⌘' },
      ],
    },
    {
      label: t('admin.sidebar.sections.administration', 'Administration'),
      items: [
        { to: '/admin/configuration', label: t('admin.sidebar.items.configuration', 'Config'), icon: '⚙' },
        { to: '/admin/finance',       label: t('admin.sidebar.items.finance', 'Finance'), icon: '◆' },
        { to: '/admin/profile',       label: t('admin.sidebar.items.profile', 'Profile'), icon: '◕' },
      ],
    },
  ], [t, hasCashback, isTcn]);

  // Close menu on navigation
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock body scroll when sheet is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {open && (
        <div className="fixed inset-0 md:hidden bg-foreground/40 z-[98]" onClick={() => setOpen(false)} />
      )}

      {/* Slide-up menu */}
      <div
        className={`fixed left-0 right-0 md:hidden bg-card border-t border-border transition-transform duration-300 z-[99] ${
          open ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{ bottom: 64, maxHeight: '70vh', overflowY: 'auto', borderRadius: '16px 16px 0 0' }}
      >
        <div className="p-4 space-y-5">
          <button
            onClick={async () => { await supabase.auth.signOut(); navigate('/login-authentication', { replace: true }); }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-error/10 border border-error/20 text-error font-semibold text-sm hover:bg-error/15 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 14H3a1 1 0 01-1-1V3a1 1 0 011-1h3M11 11l3-3-3-3M14 8H6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {t('admin.nav.logout', 'Sign out')}
          </button>

          {sections.map((section) => (
            <div key={section.label}>
              <p className="font-heading text-[10px] font-semibold uppercase tracking-[2px] text-muted-foreground/70 mb-2 px-1">
                {section.label}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {section.items.map((item) => (
                  <SheetItem
                    key={item.to}
                    to={item.to}
                    label={item.label}
                    icon={item.icon}
                    active={isActive(item.to)}
                    onClick={() => setOpen(false)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed bottom bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 md:hidden flex items-center bg-card border-t border-border z-[100]"
        style={{ height: 64, paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {quickItems.map((item) => (
          <QuickItem key={item.to} {...item} active={isActive(item.to)} />
        ))}
        <button
          onClick={() => setOpen((v) => !v)}
          className={`flex-1 flex flex-col items-center justify-center h-full gap-0.5 transition-colors ${
            open ? 'text-primary' : 'text-muted-foreground'
          }`}
          aria-label={t('admin.nav.menu', 'Menu')}
        >
          <span className="flex flex-col gap-1 w-4 items-stretch">
            <span className={`h-0.5 rounded-full transition-all ${open ? 'bg-primary translate-y-1.5 rotate-45' : 'bg-current'}`} />
            <span className={`h-0.5 rounded-full transition-all ${open ? 'opacity-0' : 'bg-current opacity-100'}`} />
            <span className={`h-0.5 rounded-full transition-all ${open ? 'bg-primary -translate-y-1.5 -rotate-45' : 'bg-current'}`} />
          </span>
          <span className="text-[10px] font-medium mt-0.5">{t('admin.nav.menu', 'Menu')}</span>
        </button>
      </nav>
    </>
  );
};

export default AdminBottomNav;
